import styled from 'foundations-theming/utils';
import { useCallback, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import PortalIdParser from 'PortalIdParser';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Button } from 'foundations-components/transitional/components/Button';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { LINKED_IN_CONNECT, LINKED_IN_MESSAGE } from 'crm-activity-creator-data/interaction/InteractionTypes';
import { useLinkedInStatus } from 'crm-activity-creator-data/hooks/useLinkedInStatus';
import PopoverActivityButton from 'crm-activity-creator-widgets/buttons/PopoverActivityButton';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledButtonWrapper = styled.span.withConfig({
  displayName: "CreateLinkedInButton__StyledButtonWrapper",
  componentId: "nhh0v8-0"
})(["font-weight:", ";font-size:", ";&:hover{background:", ";}button{width:100%;}"], ({
  theme
}) => theme.typography['body-100'].fontWeight, ({
  theme
}) => theme.typography['body-100'].fontSize, ({
  theme
}) => theme.color['fill-field-hover']);
function CreateLinkedInButton({
  interactionName,
  objectId,
  objectTypeId,
  taskId,
  buttonSize,
  isOverflow
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const sendOpenMessage = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_ACTIVITY_CREATOR);
  const {
    hasLinkedInIntegration,
    loading
  } = useLinkedInStatus();
  const portalId = PortalIdParser.get();
  const salesNavigatorUrl = `/ecosystem/${portalId}/marketplace/apps/linkedin-sales-navigator`;
  const salesNavigatorTooltip = /*#__PURE__*/_jsx(TFlex, {
    direction: "column",
    children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "createActivityButtons.tooltip.linkedIn.noSalesNavigator",
      options: {
        salesNavigatorLink: salesNavigatorUrl
      }
    })
  });
  const onSelectAction = useCallback(interactionType => {
    sendOpenMessage({
      interactionType,
      objectTypeId,
      objectId: Number(objectId)
    });
    setIsPopoverOpen(false);
  }, [objectId, objectTypeId, sendOpenMessage]);
  const handlePopoverToggle = targetState => {
    setIsPopoverOpen(targetState);
  };
  const handleButtonClick = () => {
    setIsPopoverOpen(prevIsPopoverOpen => !prevIsPopoverOpen);
  };
  const renderedLinkedInDropdownButton = useMemo(() => {
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      paddingBlock: "200",
      children: [/*#__PURE__*/_jsx(StyledButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, {
          use: "unstyled",
          paddingBlock: "200",
          paddingInlineStart: "500",
          paddingInlineEnd: "600",
          "data-selenium-test": "popover-linked-in-message",
          onClick: () => onSelectAction(LINKED_IN_MESSAGE),
          children: /*#__PURE__*/_jsx(TFlex, {
            alignItems: "start",
            color: "text-core-default",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "createActivityButtons.label.linked-in-message"
            })
          })
        })
      }), /*#__PURE__*/_jsx(StyledButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, {
          use: "unstyled",
          paddingBlock: "200",
          paddingInlineStart: "500",
          paddingInlineEnd: "600",
          "data-selenium-test": "popover-linked-in-connect",
          onClick: () => onSelectAction(LINKED_IN_CONNECT),
          children: /*#__PURE__*/_jsx(TFlex, {
            alignItems: "start",
            color: "text-core-default",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "createActivityButtons.label.linked-in-connect"
            })
          })
        })
      })]
    });
  }, [onSelectAction]);
  const tooltip = hasLinkedInIntegration ? undefined : salesNavigatorTooltip;
  return /*#__PURE__*/_jsx(PopoverActivityButton, {
    interactionName: interactionName,
    objectId: objectId,
    objectTypeId: objectTypeId,
    taskId: taskId,
    buttonSize: buttonSize,
    isLoading: loading,
    isOverflow: isOverflow,
    disabled: !hasLinkedInIntegration,
    showCarrot: true,
    tooltipMessage: tooltip,
    open: isPopoverOpen,
    onPopoverToggle: handlePopoverToggle,
    buttonProps: {
      onClick: handleButtonClick
    },
    content: renderedLinkedInDropdownButton,
    dataTestId: "linked-in-button",
    onboardingSelector: "create-engagement-linked-in",
    seleniumSelector: "create-engagement-linked-in-button"
  });
}
export default CreateLinkedInButton;